'use client'
import CtaDefault from '@/components/ui/cta/CtaDefault'
import Header from '.'
import NavContainer from './NavContainer'
import { useState, useEffect } from 'react'
import clsx from 'clsx'
import type { ProductFragment } from '@/lib/types/datocms/graphql/generated'

export default function NavHeader({
	nav,
	products,
}: Readonly<{
	nav: any
	products: ProductFragment[]
}>) {
	const [isMegaMenuOpen, setIsMegaMenuOpen] = useState(false)
	const [activeMegaMenu, setActiveMegaMenu] = useState('' as string | null | undefined)
	const [currentDropdown, setCurrentDropdown] = useState<string | null>(null)
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
	const [scrollPosition, setScrollPosition] = useState(0)

	// Don't allow the body to scroll when mobile menu is open while also preventing scroll jumps
	useEffect(() => {
		if (isMobileMenuOpen) {
			// Store the current scroll position in state
			const currentScroll = window.scrollY
			setScrollPosition(currentScroll)
			document.body.style.position = 'fixed'
			document.body.style.top = `-${currentScroll}px`
			document.body.style.width = '100%'
		} else {
			// Remove the fixed positioning styles
			document.body.style.position = ''
			document.body.style.top = ''
			document.body.style.width = ''
			// Restore the scroll position from state with instant behavior
			window.scrollTo({ top: scrollPosition, behavior: 'instant' })
		}

		return () => {
			document.body.style.position = ''
			document.body.style.top = ''
			document.body.style.width = ''
		}
	}, [isMobileMenuOpen])

	return (
		<Header
			setIsMegaMenuOpen={setIsMegaMenuOpen}
			setCurrentDropdown={setCurrentDropdown}
			setActiveMegaMenu={setActiveMegaMenu}
		>
			<nav className="flex items-center gap-6 max-lg:ml-auto max-lg:mr-3">
				{/* Top Bar Nav Items / Mobile Menu */}
				<NavContainer
					nav={nav}
					isMegaMenuOpen={isMegaMenuOpen}
					setIsMegaMenuOpen={setIsMegaMenuOpen}
					activeMegaMenu={activeMegaMenu}
					setActiveMegaMenu={setActiveMegaMenu}
					currentDropdown={currentDropdown}
					setCurrentDropdown={setCurrentDropdown}
					isMobileMenuOpen={isMobileMenuOpen}
					setIsMobileMenuOpen={setIsMobileMenuOpen}
					products={products}
				/>
			</nav>

			<div className="flex gap-2">
				{nav?.secondaryCta && (
					<CtaDefault cta={nav.secondaryCta} variant="tertiary" className="max-lg:hidden" />
				)}
				{nav?.cta && <CtaDefault cta={nav.cta} className="max-sm:hidden" />}
			</div>

			<CtaDefault
				variant="primary"
				backButton
				className={clsx(
					'absolute top-20 left-4 transition-opacity duration-300 ease-out',
					// eslint-disable-next-line no-nested-ternary
					activeMegaMenu
						? isMegaMenuOpen || isMobileMenuOpen
							? 'opacity-0 pointer-events-none'
							: 'opacity-100 pointer-events-auto delay-500'
						: 'opacity-0 pointer-events-none'
				)}
				onClick={() => {
					setIsMegaMenuOpen(true)
					setIsMobileMenuOpen(true)
				}}
			>
				Back to {activeMegaMenu}
			</CtaDefault>
		</Header>
	)
}
